/**
 * ParticleCanvas Component - Enhanced Version
 * ============================================
 * Canvas-based particle system for the hero background
 * Creates floating golden blockchain-style fragments
 * 
 * Enhanced Features:
 * - Depth illusion (background moves slowest for parallax)
 * - Better performance optimization
 * - Smoother mouse interaction
 * - Responsive particle count
 */

import React, { useEffect, useRef, useCallback } from 'react';

const ParticleCanvas = ({ isActive, isMobile, mousePosition = { x: 0, y: 0 } }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });

  // Update mouse position from props (for depth illusion - slowest movement)
  useEffect(() => {
    // Background moves slowest (multiply by small factor)
    mouseRef.current = {
      x: mousePosition.x * 8, // Slowest layer
      y: mousePosition.y * 8,
    };
  }, [mousePosition]);

  // Particle class
  class Particle {
    constructor(canvas, isMobileDevice) {
      this.canvas = canvas;
      this.isMobile = isMobileDevice;
      this.reset(true);
    }

    reset(initial = false) {
      this.x = Math.random() * this.canvas.width;
      this.y = initial
        ? Math.random() * this.canvas.height
        : this.canvas.height + 10;
      this.size = Math.random() * 3 + 1;
      this.speedY = -(Math.random() * 0.4 + 0.08);
      this.speedX = (Math.random() - 0.5) * 0.25;
      this.opacity = Math.random() * 0.5 + 0.15;
      this.rotation = Math.random() * 360;
      this.rotationSpeed = (Math.random() - 0.5) * 1.5;

      // Refined gold color palette (warm gold → amber → soft white)
      const colorChoice = Math.random();
      if (colorChoice < 0.4) {
        // Warm gold
        this.color = `hsla(43, 74%, 58%, ${this.opacity})`;
      } else if (colorChoice < 0.7) {
        // Amber
        this.color = `hsla(38, 68%, 50%, ${this.opacity})`;
      } else if (colorChoice < 0.9) {
        // Soft gold
        this.color = `hsla(45, 80%, 65%, ${this.opacity})`;
      } else {
        // Soft white gold
        this.color = `hsla(48, 60%, 75%, ${this.opacity})`;
      }

      // Shape type (square or diamond)
      this.shape = Math.random() > 0.5 ? 'square' : 'diamond';
      
      // Base position for parallax offset
      this.baseX = this.x;
      this.baseY = this.y;
    }

    update(mouseOffsetX, mouseOffsetY, canvasMouseX, canvasMouseY) {
      this.baseY += this.speedY;
      this.baseX += this.speedX;
      this.rotation += this.rotationSpeed;

      // Apply parallax offset (depth illusion)
      this.x = this.baseX + mouseOffsetX;
      this.y = this.baseY + mouseOffsetY;

      // Mouse repulsion interaction (desktop only)
      if (!this.isMobile && canvasMouseX && canvasMouseY) {
        const dx = canvasMouseX - this.x;
        const dy = canvasMouseY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 120) {
          const force = (120 - distance) / 120;
          this.x -= (dx / distance) * force * 0.4;
          this.y -= (dy / distance) * force * 0.4;
        }
      }

      // Reset if out of bounds
      if (
        this.baseY < -10 ||
        this.baseX < -10 ||
        this.baseX > this.canvas.width + 10
      ) {
        this.reset();
      }
    }

    draw(ctx) {
      ctx.save();
      ctx.translate(this.x, this.y);
      ctx.rotate((this.rotation * Math.PI) / 180);

      // Soft glow
      ctx.shadowBlur = 8;
      ctx.shadowColor = this.color;

      ctx.fillStyle = this.color;
      ctx.beginPath();

      if (this.shape === 'square') {
        ctx.rect(-this.size / 2, -this.size / 2, this.size, this.size);
      } else {
        // Diamond shape
        ctx.moveTo(0, -this.size);
        ctx.lineTo(this.size, 0);
        ctx.lineTo(0, this.size);
        ctx.lineTo(-this.size, 0);
        ctx.closePath();
      }

      ctx.fill();
      ctx.restore();
    }
  }

  // Initialize particles
  const initParticles = useCallback((canvas) => {
    const particleCount = isMobile ? 35 : 90;
    particlesRef.current = [];

    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push(new Particle(canvas, isMobile));
    }
  }, [isMobile]);

  // Draw connections between particles
  const drawConnections = useCallback((ctx) => {
    const particles = particlesRef.current;
    const maxDistance = isMobile ? 70 : 100;

    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x;
        const dy = particles[i].y - particles[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < maxDistance) {
          const opacity = (1 - distance / maxDistance) * 0.12;
          ctx.strokeStyle = `rgba(212, 168, 83, ${opacity})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(particles[j].x, particles[j].y);
          ctx.stroke();
        }
      }
    }
  }, [isMobile]);

  // Animation loop
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Clear canvas with fade effect for trails
    ctx.fillStyle = 'rgba(5, 5, 5, 0.12)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Get mouse position for repulsion
    const rect = canvas.getBoundingClientRect();
    const canvasMouseX = mouseRef.current.canvasX;
    const canvasMouseY = mouseRef.current.canvasY;

    // Update and draw particles
    particlesRef.current.forEach((particle) => {
      particle.update(
        mouseRef.current.x,
        mouseRef.current.y,
        canvasMouseX,
        canvasMouseY
      );
      particle.draw(ctx);
    });

    // Draw connection lines between nearby particles
    drawConnections(ctx);

    animationRef.current = requestAnimationFrame(animate);
  }, [drawConnections]);

  // Handle resize
  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Reinitialize particles on resize
    initParticles(canvas);
  }, [initParticles]);

  // Handle mouse move for repulsion effect
  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    mouseRef.current.canvasX = e.clientX - rect.left;
    mouseRef.current.canvasY = e.clientY - rect.top;
  }, []);

  // Setup
  useEffect(() => {
    if (!isActive) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Initialize particles
    initParticles(canvas);

    // Start animation
    animate();

    // Event listeners
    window.addEventListener('resize', handleResize);
    if (!isMobile) {
      window.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isActive, isMobile, initParticles, animate, handleResize, handleMouseMove]);

  return (
    <canvas
      ref={canvasRef}
      className="particle-canvas"
      aria-hidden="true"
    />
  );
};

export default ParticleCanvas;
